import React, { useState } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { buildDiffRows, buildQuizDiffRows } from './contentFetcher';
import { CONTENT_TYPES } from '../../constants/contentTypes';

// Colour tokens — match the app's palette
const CHANGED_BG     = '#fffbeb';
const CHANGED_LEFT   = '3px solid #f59e0b';
const UNCHANGED_LEFT = '3px solid transparent';
const CURRENT_COLOR  = '#92400e';   // amber-900 for strikethrough text
const PROPOSED_COLOR = '#15803d';   // green-700 for new value

function ValueCell({ value, changed, side }) {
  const color   = changed ? (side === 'current' ? CURRENT_COLOR : PROPOSED_COLOR) : '#374151';
  const through = changed && side === 'current';

  return (
    <Typography
      variant="body2"
      sx={{
        color,
        fontWeight: changed && side === 'proposed' ? 600 : 400,
        textDecoration: through ? 'line-through' : 'none',
        opacity: through ? 0.65 : 1,
        overflowWrap: 'anywhere',
        wordBreak: 'normal',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.5,
      }}
    >
      {value ?? '—'}
    </Typography>
  );
}

function DiffRow({ row, hasLive }) {
  const changed = row.changed && row.current !== undefined;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: hasLive ? '80px 1fr 1fr' : '90px 1fr', sm: hasLive ? '120px 1fr 1fr' : '140px 1fr' },
        gap: 0,
        px: 2,
        py: 1.25,
        bgcolor: changed ? CHANGED_BG : 'white',
        borderLeft: changed ? CHANGED_LEFT : UNCHANGED_LEFT,
        borderBottom: '1px solid #f1f5f9',
        '&:last-child': { borderBottom: 0 },
        alignItems: 'flex-start',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: '#6b7280', mt: '2px', whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}
      >
        {row.label}
      </Typography>

      {hasLive && (
        <Box sx={{ pr: 1.5 }}>
          <ValueCell value={row.current} changed={changed} side="current" />
        </Box>
      )}

      <ValueCell value={row.proposed} changed={changed} side="proposed" />
    </Box>
  );
}

export default function DiffTable({ contentType, proposedData, liveContent, onCompare }) {
  const [showUnchanged, setShowUnchanged] = useState(false);

  if (!proposedData) return null;

  // liveContent === undefined → still loading
  const isLoading = liveContent === undefined;
  const hasLive   = liveContent !== null && !isLoading;

  const liveForRows = hasLive ? liveContent : null;
  const rows = contentType === CONTENT_TYPES.GAME_SCHEDULE
    ? buildQuizDiffRows(proposedData, liveForRows)
    : buildDiffRows(proposedData, liveForRows);
  if (!rows.length) return null;

  const changedRows   = rows.filter((r) => r.changed && r.current !== undefined);
  const unchangedRows = rows.filter((r) => !(r.changed && r.current !== undefined));
  const changeCount   = changedRows.length;

  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: '#f8fafc',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Proposed Changes
          </Typography>
          {hasLive && changeCount > 0 && (
            <Chip
              label={`${changeCount} field${changeCount > 1 ? 's' : ''} changed`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
          )}
          {isLoading && (
            <CircularProgress size={12} sx={{ color: '#5383b3' }} />
          )}
        </Box>

        {/* Side-by-side button — only when live content loaded */}
        {hasLive && onCompare && (
          <Box
            onClick={onCompare}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: '#0a326d',
              px: 1,
              py: 0.4,
              borderRadius: 1,
              border: '1px solid #5383b3',
              '&:hover': { bgcolor: '#EAF0F6' },
              flexShrink: 0,
            }}
          >
            <CompareArrowsIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
              Side-by-Side
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Column headers (only when live content is available) ─────── */}
      {hasLive && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '80px 1fr 1fr', sm: '120px 1fr 1fr' },
            px: 2,
            py: 0.75,
            bgcolor: '#f1f5f9',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Field</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Current</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Proposed</Typography>
        </Box>
      )}

      {/* ── Changed rows (always visible) ───────────────────────────── */}
      {changedRows.map((row) => (
        <DiffRow key={row.key} row={row} hasLive={hasLive} />
      ))}

      {/* ── Unchanged rows (collapsible) ─────────────────────────────── */}
      {unchangedRows.length > 0 && (
        <>
          {showUnchanged && unchangedRows.map((row) => (
            <DiffRow key={row.key} row={row} hasLive={hasLive} />
          ))}
          <Box
            onClick={() => setShowUnchanged((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 2,
              py: 0.9,
              bgcolor: '#f8fafc',
              cursor: 'pointer',
              borderTop: changedRows.length > 0 ? '1px solid #e2e8f0' : 0,
              '&:hover': { bgcolor: '#EAF0F6' },
            }}
          >
            {showUnchanged
              ? <ExpandLessIcon sx={{ fontSize: 14, color: '#6b7280' }} />
              : <ExpandMoreIcon sx={{ fontSize: 14, color: '#6b7280' }} />
            }
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, fontSize: 11 }}>
              {showUnchanged
                ? 'Hide unchanged fields'
                : `${unchangedRows.length} unchanged field${unchangedRows.length > 1 ? 's' : ''}`
              }
            </Typography>
          </Box>
        </>
      )}

      {/* ── No live content note ─────────────────────────────────────── */}
      {!hasLive && !isLoading && (
        <Box sx={{ px: 2, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 11 }}>
            Live comparison unavailable for this content type — showing proposed values only.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
